// src/components/sdk/components/SdkDownloadContent.tsx

import React from 'react';
import { Card, Button, Typography, Tag, List, Alert, Upload, Space, Modal, Form, Input, Select, message, Spin, Tooltip, Divider } from 'antd';
import {
    DownloadOutlined,
    AndroidOutlined,
    AppleOutlined,
    FileTextOutlined,
    GithubOutlined,
    GlobalOutlined,
    RocketOutlined,
    CodeOutlined,
    BookOutlined,
    LinkOutlined,
    AlertOutlined,
    UploadOutlined,
    CopyOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { sdkMgmt } from '../../../server/SdkMgmt';
import { SdkDownloadContentProps, SdkDownloadContentState, SdkPackage } from '../SdkInterface';
import { UserRole } from '../../../services/Macro';
import { storageUtil } from '../utils/StorageUtil';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

// ============ 配置常量 ============
const CONFIG = {
    SHOW_ALERT: false,
    SHOW_DOC_SECTION: true,
    SHOW_QUICK_LINKS: false,
    SHOW_ANDROID_SDK: true,
    SHOW_IOS_SDK: false,
    SHOW_WEB_SDK: false,
    /** /mgmt/sdkDoc */
    DOC_URL: '/mgmt/sdkDoc',
    GITHUB_URL: 'https://github.com/example/sdk',
    CHANGELOG_URL: 'https://example.com/sdk/changelog',
    SUPPORT_EMAIL: 'support@example.com',
};

interface ExtendedSdkDownloadContentProps extends SdkDownloadContentProps {
    isMobile?: boolean;
}

export class SdkDownloadContent extends React.Component<ExtendedSdkDownloadContentProps, SdkDownloadContentState> {
    sdkFormRef = React.createRef<any>();
    docFormRef = React.createRef<any>();

    constructor(props: ExtendedSdkDownloadContentProps) {
        super(props);

        const userRole = storageUtil.getUserRoleFromStorage();

        this.state = {
            sdkPackages: [],
            isDocUploadModalVisible: false,
            isSdkUploadModalVisible: false,
            uploadingDoc: false,
            uploadingSdk: false,
            userRole: userRole,
            loading: true,
            loadError: '',
            sdkDownloadInfo: null,
            docDownloadInfo: null,
        };
    }

    componentDidMount() {
        this.fetchDownloadInfo();
    }

    fetchDownloadInfo = async () => {
        const DEBUG = true;
        const TAG = `SdkDownloadContent.fetchDownloadInfo() - `;

        this.setState({ loading: true, loadError: '' });

        try {
            const response = await sdkMgmt.getDownloadInfo();
            DEBUG && console.log(TAG, `response:\n`, response);

            if (response && response.success) {
                const data = response.data;
                const docDownloadInfo = data.docDownloadInfo;
                const sdkDownloadInfo = data.sdkDownloadInfo;

                let sdkPackages: SdkPackage[] = [];

                if (sdkDownloadInfo && sdkDownloadInfo.downloadUrl) {
                    const version = sdkDownloadInfo.version;
                    let platform: 'android' | 'ios' | 'web' = sdkDownloadInfo.platform;
                    let description: string = sdkDownloadInfo.description;
                    if (!description) description = `SDK 下载包${sdkDownloadInfo.size ? `，大小 ${this.formatFileSize(sdkDownloadInfo.size)}` : ''}`;

                    sdkPackages = [{
                        id: '1',
                        name: 'Android SDK',
                        platform: platform,
                        version: version,
                        size: this.formatFileSize(sdkDownloadInfo.size || 0),
                        updateTime: sdkDownloadInfo.updateTime || new Date().toLocaleString('zh-CN'),
                        downloadUrl: sdkDownloadInfo.downloadUrl,
                        description: description,
                    },
                    {
                        id: '2',
                        name: 'iOS SDK',
                        platform: 'ios',
                        version: 'sdk.download.notReleased',
                        size: this.formatFileSize(0),
                        updateTime: '-',
                        downloadUrl: null,
                        description: 'sdk.download.noSdkDesc',
                    }];
                } else {
                    sdkPackages = [];
                }

                localStorage.setItem('doc_download_url', docDownloadInfo?.downloadUrl || '');

                this.setState({
                    sdkPackages: sdkPackages,
                    docDownloadInfo: docDownloadInfo || null,
                    loading: false,
                });
            } else {
                this.setState({
                    sdkPackages: [],
                    loading: false,
                    loadError: response?.message || '获取下载信息失败',
                });
            }
        } catch (error: any) {
            DEBUG && console.error(TAG, `error:\n`, error);
            this.setState({
                sdkPackages: [],
                loading: false,
                loadError: error.message || '网络错误，请稍后重试',
            });
        }
    };

    formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    isAdmin = (): boolean => {
        const { userRole } = this.state;
        const isAdmin = userRole === UserRole.ADMIN;
        console.log('当前用户角色:', userRole, '是否为管理员:', isAdmin);
        return isAdmin;
    };

    getPlatformIcon = (platform: string) => {
        switch (platform) {
            case 'android':
                return <AndroidOutlined style={{ color: '#3DDC84', fontSize: 20 }} />;
            case 'ios':
                return <AppleOutlined style={{ color: '#555555', fontSize: 20 }} />;
            case 'web':
                return <GlobalOutlined style={{ color: '#3178C6', fontSize: 20 }} />;
            default:
                return <CodeOutlined />;
        }
    };

    getPlatformTag = (platform: string) => {
        const config: Record<string, { color: string; text: string }> = {
            android: { color: 'green', text: 'Android' },
            ios: { color: 'blue', text: 'iOS' },
            web: { color: 'purple', text: 'Web' },
        };
        const info = config[platform];
        return info ? <Tag color={info.color}>{info.text}</Tag> : <Tag>{platform}</Tag>;
    };

    handleCopyLink = (url: string, name: string) => {
        if (!url) {
            message.warning('链接为空，无法复制');
            return;
        }
        navigator.clipboard.writeText(url).then(() => {
            message.success(`${name} 链接已复制到剪贴板`);
        }).catch(() => {
            const textarea = document.createElement('textarea');
            textarea.value = url;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            message.success(`${name} 链接已复制到剪贴板`);
        });
    };

    handleDownload = (url: string, name: string) => {
        if (!url) {
            message.warning('下载链接为空');
            return;
        }
        window.open(url, '_blank');
        console.log(`下载: ${name}`);
    };

    getTimestamp = (): string => {
        const now = new Date();
        return now.getFullYear() + '-' +
            String(now.getMonth() + 1).padStart(2, '0') + '-' +
            String(now.getDate()).padStart(2, '0') + '_' +
            String(now.getHours()).padStart(2, '0') + '-' +
            String(now.getMinutes()).padStart(2, '0') + '-' +
            String(now.getSeconds()).padStart(2, '0');
    };

    handleSdkUpload = async () => {
        const DEBUG = true;
        const TAG = `SdkDownloadContent.handleSdkUpload() - `;
        try {
            const form = this.sdkFormRef.current;
            if (!form) return;

            const values = await form.validateFields();
            this.setState({ uploadingSdk: true });

            const params = {
                platform: values.platform,
                version: values.version,
                description: values.description || '',
                file: values.file?.[0]?.originFileObj,
            };
            DEBUG && console.log(TAG, `params:\n`, params);

            const response = await sdkMgmt.uploadSdk(params);
            DEBUG && console.log(TAG, `response:\n`, response);

            if (response && response.success) {
                message.success(`SDK 上传成功，正在刷新信息...`);
                await this.fetchDownloadInfo();
                form.resetFields();
                this.setState({ isSdkUploadModalVisible: false });
            } else {
                message.error(response?.message || '上传失败，请稍后重试');
            }

        } catch (error: any) {
            if (error.errorFields) {
                return;
            }
            DEBUG && console.error(TAG, `error:\n`, error);
            message.error('上传失败，请稍后重试');
        } finally {
            this.setState({ uploadingSdk: false });
        }
    };

    showSdkUploadModal = () => {
        this.setState({ isSdkUploadModalVisible: true });
        const form = this.sdkFormRef.current;
        if (form) {
            form.resetFields();
        }
    };

    handleSdkUploadCancel = () => {
        this.setState({ isSdkUploadModalVisible: false });
        const form = this.sdkFormRef.current;
        if (form) {
            form.resetFields();
        }
    };

    showDocUploadModal = () => {
        this.setState({ isDocUploadModalVisible: true });
        const form = this.docFormRef.current;
        if (form) {
            form.resetFields();
        }
    };

    handleDocUploadCancel = () => {
        this.setState({ isDocUploadModalVisible: false });
        const form = this.docFormRef.current;
        if (form) {
            form.resetFields();
        }
    };

    handleDocUpload = async () => {
        const DEBUG = true;
        const TAG = `SdkDownloadContent.handleDocUpload() - `;
        try {
            const form = this.docFormRef.current;
            if (!form) return;

            const values = await form.validateFields();
            this.setState({ uploadingDoc: true });

            const params = {
                title: values.title,
                category: values.category,
                description: values.description,
                file: values.file[0].originFileObj,
            };
            DEBUG && console.log(TAG, `params:\n`, params);

            const response = await sdkMgmt.uploadDoc(params);
            DEBUG && console.log(TAG, `response:\n`, response);

            if (response && response.success) {
                message.success(`文档 "${values.title}" 上传成功，正在刷新信息...`);
                await this.fetchDownloadInfo();
                this.setState({ isDocUploadModalVisible: false });
                form.resetFields();
            } else {
                message.error(response?.message || '上传失败，请稍后重试');
            }

        } catch (error: any) {
            if (error.errorFields) {
                return;
            }
            message.error('上传失败，请稍后重试');
        } finally {
            this.setState({ uploadingDoc: false });
        }
    };

    getUploadProps = (accept?: string): UploadProps => ({
        name: 'file',
        multiple: false,
        accept: accept,
        beforeUpload: (file) => {
            const isLt10M = file.size / 1024 / 1024 < 200;
            if (!isLt10M) {
                message.error('文件大小不能超过 200MB');
                return false;
            }
            return false;
        },
        onChange: (info) => {
            if (info.file.status === 'removed') {
            }
        },
    });

    renderDocSection = () => {
        if (!CONFIG.SHOW_DOC_SECTION) {
            return null;
        }

        const { t, isMobile = false } = this.props;
        const { docDownloadInfo } = this.state;
        const isAdmin = this.isAdmin();

        const docUrl = `https://ctrl.healthassistant.app${CONFIG.DOC_URL}`;
        const docAndroid = `${docUrl}?platform=android`;
        const docIos = `${docUrl}?platform=ios`;
        const docSize = docDownloadInfo ? this.formatFileSize(docDownloadInfo.size) : '';

        return (
            <Card
                style={{ marginBottom: 16 }}
                bodyStyle={{ padding: isMobile ? '16px' : '20px 24px' }}
            >
                <div style={{
                    display: 'flex',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 12,
                    flexDirection: isMobile ? 'column' : 'row',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <BookOutlined style={{ fontSize: 24, color: '#667eea' }} />
                        <div>
                            <Title level={isMobile ? 5 : 5} style={{ margin: 0 }}>
                                {t('sdk.download.docTitle') || '使用文档'}
                            </Title>
                        </div>
                    </div>
                    <Space style={{ width: isMobile ? '100%' : 'auto', flexWrap: 'wrap' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 24,
                            padding: '4px 12px',
                            background: '#f5f5f5',
                            borderRadius: 6,
                            flex: isMobile ? 1 : undefined,
                            overflow: 'hidden',
                            minWidth: isMobile ? 0 : undefined,
                            flexWrap: isMobile ? 'wrap' : 'nowrap',
                        }}>
                            <Text
                                style={{
                                    fontSize: isMobile ? 12 : 13,
                                    color: '#1890ff',
                                    cursor: 'pointer',
                                    wordBreak: 'break-all',
                                    whiteSpace: isMobile ? 'normal' : 'nowrap',
                                }}
                                onClick={() => docAndroid && window.open(docAndroid, '_blank')}
                            >
                                {/* {docUrl || '暂无文档链接'} */}
                                {t('sdk.download.androidGuide')}
                            </Text>
                            <Text
                                style={{
                                    fontSize: isMobile ? 12 : 13,
                                    color: '#1890ff',
                                    cursor: 'pointer',
                                    wordBreak: 'break-all',
                                    whiteSpace: isMobile ? 'normal' : 'nowrap',
                                }}
                                onClick={() => docIos && window.open(docIos, '_blank')}
                            >
                                {/* {docUrl || '暂无文档链接'} */}
                                {t('sdk.download.iosGuide')}
                            </Text>
                            <Tooltip title="复制链接">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={() => this.handleCopyLink(docUrl, '文档')}
                                    disabled={!docUrl}
                                    style={{ flexShrink: 0 }}
                                />
                            </Tooltip>
                        </div>
                        {isAdmin && (
                            <Button
                                type="default"
                                icon={<UploadOutlined />}
                                onClick={this.showDocUploadModal}
                                style={isMobile ? { width: '100%' } : {}}
                            >
                                {t('sdk.download.uploadDoc') || '上传文档'}
                            </Button>
                        )}
                    </Space>
                </div>
            </Card>
        );
    };

    renderPackageList = () => {
        const { t, isMobile = false } = this.props;
        const { sdkPackages } = this.state;
        const isAdmin = this.isAdmin();

        if (sdkPackages.length === 0) {
            return (
                <Alert
                    message="暂无可用 SDK"
                    description="当前没有可下载的 SDK 包，请上传新的 SDK 版本。"
                    type="warning"
                    showIcon
                />
            );
        }

        return (
            <List
                grid={{ gutter: 16, xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1 }}
                dataSource={sdkPackages}
                renderItem={(item) => (
                    <List.Item>
                        <Card
                            hoverable
                            style={{
                                borderRadius: 12,
                                border: '1px solid #f0f0f0',
                            }}
                            bodyStyle={{ padding: isMobile ? '16px' : '20px 24px' }}
                        >
                            <div style={{
                                display: 'flex',
                                alignItems: isMobile ? 'flex-start' : 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 12,
                                flexDirection: isMobile ? 'column' : 'row',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16, width: isMobile ? '100%' : 'auto' }}>
                                    <div style={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 12,
                                        background: '#f5f5f5',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        {this.getPlatformIcon(item.platform)}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                            <Text strong style={{ fontSize: isMobile ? 15 : 16 }}>{item.name}</Text>
                                            {this.getPlatformTag(item.platform)}
                                            <Tag color="geekblue">{t(item.version)}</Tag>
                                        </div>
                                        <div style={{ marginTop: 4 }}>
                                            <Text type="secondary" style={{ fontSize: isMobile ? 12 : 13 }}>
                                                {t(item.description)}
                                            </Text>
                                        </div>
                                        <div style={{ marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                            <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12 }}>
                                                <RocketOutlined /> {t('sdk.download.size') || '大小'}: {item.size}
                                            </Text>
                                            <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12 }}>
                                                <FileTextOutlined /> {t('sdk.download.updateTime') || '更新时间'}: {item.updateTime}
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                                <Space style={{ width: isMobile ? '100%' : 'auto', flexWrap: 'wrap' }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        flex: isMobile ? 1 : undefined,
                                        overflow: 'hidden',
                                        minWidth: 0,
                                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                                        width: isMobile ? '100%' : 'auto',
                                    }}>
                                        <Text
                                            style={{
                                                fontSize: isMobile ? 11 : 13,
                                                color: '#1890ff',
                                                cursor: 'pointer',
                                                wordBreak: 'break-all',
                                                whiteSpace: isMobile ? 'normal' : 'nowrap',
                                                flex: 1,
                                            }}
                                            onClick={() => this.handleDownload(item.downloadUrl, item.name)}
                                        >
                                            {item.downloadUrl || t('sdk.download.noDownloadLink')}
                                        </Text>
                                        <Tooltip title="复制链接">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<CopyOutlined />}
                                                onClick={() => this.handleCopyLink(item.downloadUrl, 'SDK')}
                                                disabled={!item.downloadUrl}
                                                style={{ flexShrink: 0 }}
                                            />
                                        </Tooltip>
                                    </div>
                                    {isAdmin && (
                                        <Button
                                            type="default"
                                            icon={<UploadOutlined />}
                                            onClick={this.showSdkUploadModal}
                                            style={isMobile ? { width: '100%' } : {}}
                                        >
                                            {t('sdk.download.uploadSdk') || '上传'}
                                        </Button>
                                    )}
                                </Space>
                            </div>
                        </Card>
                    </List.Item>
                )}
            />
        );
    };

    renderQuickLinks = () => {
        if (!CONFIG.SHOW_QUICK_LINKS) {
            return null;
        }

        const { t, isMobile = false } = this.props;

        return (
            <>
                <Divider />
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                    gap: isMobile ? 8 : 16,
                    marginTop: 8,
                }}>
                    <Card
                        size="small"
                        bodyStyle={{ padding: '16px 20px' }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <GithubOutlined style={{ fontSize: 20, color: '#333' }} />
                            <div>
                                <Text strong>{t('sdk.download.sourceCode') || '源码仓库'}</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    <a href={CONFIG.GITHUB_URL} target="_blank" rel="noopener noreferrer">
                                        {CONFIG.GITHUB_URL.replace('https://', '')}
                                    </a>
                                </Text>
                            </div>
                        </div>
                    </Card>
                    <Card
                        size="small"
                        bodyStyle={{ padding: '16px 20px' }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <FileTextOutlined style={{ fontSize: 20, color: '#667eea' }} />
                            <div>
                                <Text strong>{t('sdk.download.changelog') || '更新日志'}</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    <a href={CONFIG.CHANGELOG_URL} target="_blank" rel="noopener noreferrer">
                                        {t('sdk.download.viewChangelog') || '查看更新日志'}
                                    </a>
                                </Text>
                            </div>
                        </div>
                    </Card>
                    <Card
                        size="small"
                        bodyStyle={{ padding: '16px 20px' }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <AlertOutlined style={{ fontSize: 20, color: '#faad14' }} />
                            <div>
                                <Text strong>{t('sdk.download.support') || '技术支持'}</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    <a href={`mailto:${CONFIG.SUPPORT_EMAIL}`}>{CONFIG.SUPPORT_EMAIL}</a>
                                </Text>
                            </div>
                        </div>
                    </Card>
                </div>
            </>
        );
    };

    renderSdkUploadModal = () => {
        const { t, isMobile = false } = this.props;
        const { isSdkUploadModalVisible, uploadingSdk } = this.state;

        return (
            <Modal
                title={t('sdk.download.uploadSdk') || '上传 SDK'}
                open={isSdkUploadModalVisible}
                onOk={this.handleSdkUpload}
                onCancel={this.handleSdkUploadCancel}
                confirmLoading={uploadingSdk}
                width={isMobile ? '95%' : 600}
                okText={t('sdk.download.confirmUpload') || '确认上传'}
                cancelText={t('sdk.download.cancel') || '取消'}
            >
                <Form
                    ref={this.sdkFormRef}
                    layout="vertical"
                >
                    <Form.Item
                        name="platform"
                        label={t('sdk.download.platform') || '平台'}
                        rules={[{ required: true, message: '请选择平台' }]}
                    >
                        <Select placeholder="请选择平台">
                            <Option value="android">Android</Option>
                            <Option value="ios">iOS</Option>
                            <Option value="web">Web</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="version"
                        label={t('sdk.download.version') || '版本号'}
                        rules={[{ required: true, message: '请输入版本号' }]}
                    >
                        <Input placeholder="请输入版本号，如 v2.5.1" />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label={t('sdk.download.description') || '描述'}
                    >
                        <Input.TextArea rows={3} placeholder="请输入 SDK 描述信息" />
                    </Form.Item>
                    <Form.Item
                        name="file"
                        label={t('sdk.download.sdkFile') || 'SDK 文件'}
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e && e.fileList}
                    >
                        <Upload {...this.getUploadProps()} maxCount={1}>
                            <Button icon={<UploadOutlined />}>选择文件</Button>
                        </Upload>
                    </Form.Item>
                </Form>
            </Modal>
        );
    };

    renderDocUploadModal = () => {
        const { t, isMobile = false } = this.props;
        const { isDocUploadModalVisible, uploadingDoc } = this.state;

        return (
            <Modal
                title={t('sdk.download.uploadDoc') || '上传文档'}
                open={isDocUploadModalVisible}
                onOk={this.handleDocUpload}
                onCancel={this.handleDocUploadCancel}
                confirmLoading={uploadingDoc}
                width={isMobile ? '95%' : 600}
                okText={t('sdk.download.confirmUpload') || '确认上传'}
                cancelText={t('sdk.download.cancel') || '取消'}
            >
                <Form
                    ref={this.docFormRef}
                    layout="vertical"
                >
                    <Form.Item
                        name="title"
                        label={t('sdk.download.docTitle') || '文档标题'}
                        rules={[{ required: true, message: '请输入文档标题' }]}
                    >
                        <Input placeholder="请输入文档标题" />
                    </Form.Item>
                    <Form.Item
                        name="category"
                        label={t('sdk.download.docCategory') || '文档分类'}
                        rules={[{ required: true, message: '请选择文档分类' }]}
                    >
                        <Select placeholder="请选择文档分类">
                            <Option value="guide">集成指南</Option>
                            <Option value="api">API 文档</Option>
                            <Option value="faq">常见问题</Option>
                            <Option value="changelog">更新日志</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label={t('sdk.download.description') || '描述'}
                    >
                        <Input.TextArea rows={3} placeholder="请输入文档描述信息" />
                    </Form.Item>
                    <Form.Item
                        name="file"
                        label={t('sdk.download.docFile') || '文档文件'}
                        rules={[{ required: true, message: '请上传文档文件' }]}
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e && e.fileList}
                    >
                        <Upload {...this.getUploadProps('.pdf,.doc,.docx,.md')} maxCount={1}>
                            <Button icon={<UploadOutlined />}>选择文件</Button>
                        </Upload>
                    </Form.Item>
                </Form>
            </Modal>
        );
    };

    renderAlert = () => {
        if (!CONFIG.SHOW_ALERT) {
            return null;
        }

        const { t } = this.props;

        return (
            <Alert
                style={{ marginTop: 24 }}
                message={t('sdk.download.tipTitle') || '使用提示'}
                description={
                    <div>
                        <Text>
                            {t('sdk.download.tipContent') ||
                                '1. 请确认您的开发环境满足 SDK 的最低版本要求。\n2. 下载后请参考使用文档进行集成。\n3. 如遇到问题，请查看常见问题或联系技术支持。'}
                        </Text>
                    </div>
                }
                type="info"
                showIcon
            />
        );
    };

    render() {
        const { isMobile = false } = this.props;
        const { loading, loadError } = this.state;

        if (loading) {
            return (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 400,
                }}>
                    <Spin size="large" tip="加载下载信息..." />
                </div>
            );
        }

        const showErrorAlert = loadError && this.state.sdkPackages.length > 0;

        return (
            <div style={{ padding: isMobile ? '0' : undefined }}>
                {showErrorAlert && (
                    <Alert
                        style={{ marginBottom: 16 }}
                        message="加载提示"
                        description={loadError}
                        type="warning"
                        showIcon
                        closable
                        onClose={() => this.setState({ loadError: '' })}
                    />
                )}
                {this.renderDocSection()}
                {this.renderPackageList()}
                {this.renderQuickLinks()}
                {this.renderAlert()}
                {this.renderSdkUploadModal()}
                {this.renderDocUploadModal()}
            </div>
        );
    }
}

export default SdkDownloadContent;